import { useRef, useState } from "react";
import type { ReactNode, TouchEvent } from "react";
import { LoaderCircle } from "lucide-react";

const THRESHOLD = 72;

/**
 * Minimal pull-to-refresh for list screens (brief §6 global rules): drag down
 * from the top past the threshold → run `onRefresh`. Native scroll unaffected.
 */
export function PullToRefresh({ onRefresh, children }: { onRefresh: () => Promise<unknown>; children: ReactNode }) {
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (window.scrollY <= 0 && !busy) startY.current = e.touches[0]?.clientY ?? null;
  };
  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (startY.current === null) return;
    const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
    setPull(dy > 0 && window.scrollY <= 0 ? Math.min(dy * 0.5, THRESHOLD * 1.5) : 0);
  };
  const onTouchEnd = () => {
    const release = pull;
    startY.current = null;
    setPull(0);
    if (release >= THRESHOLD && !busy) {
      setBusy(true);
      void onRefresh().finally(() => setBusy(false));
    }
  };

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-testid="ec-pull-refresh">
      {(pull > 0 || busy) && (
        <div aria-hidden className="flex justify-center overflow-hidden text-fg-muted" style={{ height: busy ? 32 : pull * 0.6 }}>
          <LoaderCircle size={20} className={busy ? "animate-spin" : ""} style={{ transform: `rotate(${pull * 3}deg)` }} />
        </div>
      )}
      {children}
    </div>
  );
}
